import type { Estimate, Prisma } from '@prisma/client';
import { EstimateLineItemType, EstimateState, PricingType } from '@prisma/client';
import type { Decimal } from '@prisma/client/runtime/library';
import type { EstimateLineItemFull } from '@services/estimate.service';
import { fractionToDecimal, parseFraction } from '@utils/fraction';
import { getPrefixedName, getPrefixedNames } from '@utils/reactHookForm';
import type { EstimateForFocus } from '~/server/schema/estimate.schema';
import { isNotNullish } from './types';

export interface ISquareFeetFactors {
	fractionWidth: string;
	fractionHeight: string;
}

export interface IBlockSquareFeetFactors {
	blockWidth: number;
	blockHeight: number;
}

export interface ISquareFeetResult {
	squareFeet: number;
	decimalWidth: number;
	decimalHeight: number;
}

interface ISquareFeetLineItemCostFactors {
	calculatedSquareFeet?: number;
	minimumSquareFeet?: number;
	quantity: number;
	unitPrice?: number;
}

export interface IPurchaseOrderLineItemCostFactors {
	priceType: PricingType;
	unitPrice: number;
	quantity: number;
	squareFeet?: number;
	minimumSquareFeet?: number;
	surcharge?: number;
}

export interface IMuntinBars {
	horizontalBars?: number | null;
	verticalBars?: number | null;
}

interface IFabricationCostFactors extends IMuntinBars {
	length?: Prisma.Decimal | number | null;
	quantity?: Prisma.Decimal | number | null;
	supplierInventory?: { priceType: PricingType } | null;
	unitPrice?: Prisma.Decimal | number | null;
}

export interface IEstimateLineItemCostFactors {
	priceType: PricingType;
	unitPrice: number;
	vendorPrice?: Prisma.Decimal | null;
	markup: number;
	quantity: number;
	price?: number;
	blockWidth?: number;
	blockHeight?: number;
	blockSquareFeet?: number;
	minimumSquareFeet?: number;
	numberOfTechnicians?: number;
	estimateLineItemType?: string;
	surcharge?: number;
	isSellPrice: boolean;
	shouldRecalculateBasedOnMarkup?: boolean;
	length?: number;
}

interface IEstimateLineItemCostResult {
	rawCost: number;
	totalCost: number;
	updatedSellPrice?: number;
}

interface ICalculateMarkupFactors {
	sellPrice: number;
	price: number;
	surcharge: number;
}

interface IMarkupFactors {
	markup: number;
	costBeforeMarkup: number;
}

interface ICalculateTotalCostFromMarkupFactors {
	markup: number;
	rawCost: number;
	surcharge: number;
}

export const calculateMarkup = ({ sellPrice, price, surcharge }: ICalculateMarkupFactors) => {
	const newPrice = price === 0 ? sellPrice : price;
	const priceAndSurcharge = newPrice + surcharge;

	if (priceAndSurcharge === 0) return 100;

	return roundAmount(((sellPrice - priceAndSurcharge) / priceAndSurcharge) * 100, 3);
};

export const addMarkup = ({ markup, costBeforeMarkup }: IMarkupFactors): number => {
	return (markup * costBeforeMarkup) / 100 + costBeforeMarkup;
};

const calculateTotalCostFromMarkup = ({ markup, rawCost, surcharge }: ICalculateTotalCostFromMarkupFactors): number => {
	const rawCostAndSurcharge = rawCost + surcharge;

	return roundAmount((markup / 100) * rawCostAndSurcharge + rawCostAndSurcharge, 2);
};

export const roundAmount = (amount: number, precision: number) => {
	return Number(amount.toFixed(precision));
};

export function money(amount: number) {
	return Number.parseFloat(amount.toFixed(2));
}

/**
 * Map a free-text unit-of-measure string (e.g. from a supplier document or spreadsheet) to a PricingType.
 * Defaults to EACH for anything unrecognized or missing — a UOM must explicitly read as square-foot,
 * length, hourly, etc. to map to those types. Never infer SQUAREFOOT from the presence of dimensions.
 */
export const parseUnitOfMeasure = (uom: string | null | undefined): PricingType => {
	if (!uom) return PricingType.EACH;
	const normalized = uom
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9/]/g, '');
	const sqftAliases = ['sqft', 'sq/ft', 'squarefoot', 'squarefeet', 'sqfoot', 'sqfeet', 'sf'];
	if (sqftAliases.includes(normalized)) return PricingType.SQUAREFOOT;
	const lengthAliases = ['length', 'len', 'ln', 'ft', 'feet', 'foot', 'linearft', 'linearfoot', 'lft'];
	if (lengthAliases.includes(normalized)) return PricingType.LENGTH;
	const hourlyAliases = ['hour', 'hourly', 'hr', 'hrs', 'hours'];
	if (hourlyAliases.includes(normalized)) return PricingType.HOURLY;
	const linearInchAliases = ['linearinch', 'lininch', 'lin', 'linearinches'];
	if (linearInchAliases.includes(normalized)) return PricingType.LINEAR_INCH;
	return PricingType.EACH;
};

/**
 * OTHER line items only support SQUAREFOOT or EACH pricing, so collapse any other UOM to EACH.
 * SQUAREFOOT is used only when the UOM explicitly reads as square-foot — never inferred from dimensions.
 */
export const parseOtherLineItemUnitOfMeasure = (uom: string | null | undefined): PricingType =>
	parseUnitOfMeasure(uom) === PricingType.SQUAREFOOT ? PricingType.SQUAREFOOT : PricingType.EACH;

// NOTE width + height inputs are assumed to be in inches
export const calculateSquareFeetFromFractions = ({ fractionWidth, fractionHeight }: ISquareFeetFactors): ISquareFeetResult | undefined => {
	const parsedWidth = parseFraction(fractionWidth);
	const parsedHeight = parseFraction(fractionHeight);

	if (parsedWidth !== undefined && parsedHeight !== undefined) {
		const decimalWidth = fractionToDecimal(parsedWidth);
		const decimalHeight = fractionToDecimal(parsedHeight);

		const widthInFeet = decimalWidth / 12;
		const heightInFeet = decimalHeight / 12;

		const squareFeet = widthInFeet * heightInFeet;

		return {
			squareFeet: roundAmount(squareFeet, 2),
			decimalWidth: decimalWidth,
			decimalHeight: decimalHeight,
		};
	} else {
		return undefined;
	}
};

// NOTE blockWidth + blockHeight are in inches
export const calculateBlockSquareFeet = ({ blockWidth, blockHeight }: IBlockSquareFeetFactors): number => {
	const blockWidthFeet = blockWidth / 12;
	const blockHeightFeet = blockHeight / 12;

	return Number((blockWidthFeet * blockHeightFeet).toFixed(2));
};

export const calculateBlockSizeWithInchNumbers = (measurementVal: number): number => {
	return 2 * Math.ceil(measurementVal / 2);
};

export const calculateBlockSizeInches = (measurementFraction: string | Decimal): number | undefined => {
	if (typeof measurementFraction !== 'string' && measurementFraction?.toString()) {
		measurementFraction = measurementFraction.toString();
	}
	const parsedMeasurementFraction = parseFraction(measurementFraction);

	if (parsedMeasurementFraction !== undefined) {
		return 2 * Math.ceil(fractionToDecimal(parsedMeasurementFraction) / 2);
	} else {
		return undefined;
	}
};

export const castFractionToDecimal = (fraction: string): number | undefined => {
	const parsedFraction = parseFraction(fraction);

	if (parsedFraction === undefined) return undefined;

	return fractionToDecimal(parsedFraction);
};

export const resolveMinimumSquareFeet = ({
	price,
	supplierInventory,
}: {
	price?: { minimumSquareFeet?: Prisma.Decimal | number | null } | null;
	supplierInventory?: { minimumSquareFeet?: Prisma.Decimal | number | null } | null;
}): number | undefined => {
	const minimumSquareFeet = price?.minimumSquareFeet ?? supplierInventory?.minimumSquareFeet;

	return isNotNullish(minimumSquareFeet) ? Number(minimumSquareFeet) : undefined;
};

export const calculateRawSquarefootLineItemCost = (costFactors: ISquareFeetLineItemCostFactors) => {
	if (costFactors.calculatedSquareFeet === undefined || costFactors.unitPrice === undefined || costFactors.quantity === undefined) {
		return undefined;
	}
	const squareFeet = Math.max(costFactors.calculatedSquareFeet ?? 0, costFactors.minimumSquareFeet ?? 0);
	const squareFootCost = costFactors.unitPrice * squareFeet;
	return squareFootCost * costFactors.quantity;
};

export const calculateEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors): IEstimateLineItemCostResult | undefined => {
	switch (costFactors.priceType) {
		case 'LENGTH':
			return calculatePerLengthEstimateLineItemCost(costFactors);
		case 'EACH':
			return calculatePerEachEstimateLineItemCost(costFactors);
		case 'HOURLY':
			if (costFactors.estimateLineItemType === 'OTHER') {
				return calculatePerEachEstimateLineItemCost(costFactors);
			} else {
				return calculateLaborEstimateLineItemCost(costFactors);
			}
		case 'LINEAR_INCH':
			return calculateLinearInchEstimateLineItemCost(costFactors);
		case 'UNITED_INCH':
			return calculateUnitedInchEstimateLineItemCost(costFactors);
		case 'SQUAREFOOT':
			return calculateSquarefootEstimateLineItemCost(costFactors);
		default:
			return undefined;
	}
};

export const calculateSquarefootEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors): IEstimateLineItemCostResult | undefined => {
	let rawCost = calculateRawSquarefootLineItemCost({
		calculatedSquareFeet: costFactors.blockSquareFeet,
		minimumSquareFeet: costFactors.minimumSquareFeet,
		quantity: costFactors.quantity,
		unitPrice: costFactors.unitPrice,
	});

	if (rawCost === undefined) {
		return undefined;
	}

	const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

	let totalCost = 0;
	let updatedSellPrice: number | undefined = undefined;
	if (costFactors.isSellPrice) {
		totalCost = rawCost;
		const vendorUnitPrice = Number(costFactors.vendorPrice ?? 0);
		rawCost = calculateRawSquarefootLineItemCost({
			calculatedSquareFeet: costFactors.blockSquareFeet,
			minimumSquareFeet: costFactors.minimumSquareFeet,
			quantity: costFactors.quantity,
			unitPrice: vendorUnitPrice,
		});
		if (rawCost === undefined) {
			return undefined;
		}

		if (costFactors.shouldRecalculateBasedOnMarkup) {
			totalCost = calculateTotalCostFromMarkup({ markup: costFactors.markup, rawCost, surcharge });

			const squareFeet = Math.max(costFactors.blockSquareFeet ?? 0, costFactors.minimumSquareFeet ?? 0);
			updatedSellPrice = totalCost / squareFeet / costFactors.quantity;
		}
	} else {
		totalCost = addMarkup({ costBeforeMarkup: rawCost + surcharge, markup: costFactors.markup });
	}

	return {
		rawCost: roundAmount(rawCost, 2),
		totalCost: roundAmount(totalCost, 2),
		updatedSellPrice: updatedSellPrice ? roundAmount(updatedSellPrice, 2) : undefined,
	};
};

export const calculateLinearInchEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors): IEstimateLineItemCostResult | undefined => {
	if (!costFactors.blockWidth || !costFactors.blockHeight) return undefined;

	let rawCost = costFactors.unitPrice * costFactors.quantity * calculateLinearInch(costFactors.blockWidth, costFactors.blockHeight);

	if (rawCost === undefined) return undefined;

	const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

	let totalCost = 0;
	let updatedSellPrice: number | undefined = undefined;
	if (costFactors.isSellPrice) {
		totalCost = rawCost;
		const vendorUnitPrice = Number(costFactors.vendorPrice ?? 0);
		rawCost = vendorUnitPrice * costFactors.quantity * calculateLinearInch(costFactors.blockWidth, costFactors.blockHeight);
		if (rawCost === undefined) return undefined;

		if (costFactors.shouldRecalculateBasedOnMarkup) {
			totalCost = calculateTotalCostFromMarkup({ markup: costFactors.markup, rawCost, surcharge });

			updatedSellPrice =
				totalCost / costFactors.unitPrice / costFactors.quantity / calculateLinearInch(costFactors.blockWidth, costFactors.blockHeight);
		}
	} else {
		totalCost = addMarkup({ costBeforeMarkup: rawCost + surcharge, markup: costFactors.markup });
	}

	return {
		rawCost: roundAmount(rawCost, 2),
		totalCost: roundAmount(totalCost, 2),
		updatedSellPrice: updatedSellPrice ? roundAmount(updatedSellPrice, 2) : undefined,
	};
};

export const calculateUnitedInchEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors): IEstimateLineItemCostResult | undefined => {
	if (!costFactors.blockWidth || !costFactors.blockHeight) return undefined;

	let rawCost = costFactors.unitPrice * costFactors.quantity * calculateUnitedInch(costFactors.blockWidth, costFactors.blockHeight);

	if (rawCost === undefined) return undefined;

	const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

	let totalCost = 0;
	let updatedSellPrice: number | undefined = undefined;
	if (costFactors.isSellPrice) {
		totalCost = rawCost;
		const vendorUnitPrice = Number(costFactors.vendorPrice ?? 0);
		rawCost = vendorUnitPrice * costFactors.quantity * calculateUnitedInch(costFactors.blockWidth, costFactors.blockHeight);
		if (rawCost === undefined) return undefined;

		if (costFactors.shouldRecalculateBasedOnMarkup) {
			totalCost = calculateTotalCostFromMarkup({ markup: costFactors.markup, rawCost, surcharge });

			updatedSellPrice =
				totalCost / costFactors.unitPrice / costFactors.quantity / calculateUnitedInch(costFactors.blockWidth, costFactors.blockHeight);
		}
	} else {
		totalCost = addMarkup({ costBeforeMarkup: rawCost + surcharge, markup: costFactors.markup });
	}

	return {
		rawCost: roundAmount(rawCost, 2),
		totalCost: roundAmount(totalCost, 2),
		updatedSellPrice: updatedSellPrice ? roundAmount(updatedSellPrice, 2) : undefined,
	};
};

export const calculatePerLengthEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors) => {
	if (!costFactors.length) return undefined;

	let rawCost = costFactors.unitPrice * costFactors.quantity * costFactors.length;
	const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

	const recalcBasedOnMarkup = costFactors.markup > 0 && rawCost > 0 && surcharge > 0 && costFactors.shouldRecalculateBasedOnMarkup;

	let totalCost = 0;
	let updatedSellPrice: number | undefined = undefined;

	if (costFactors.isSellPrice) {
		totalCost = rawCost;

		const vendorUnitPrice = Number(costFactors.vendorPrice ?? 0);
		rawCost = vendorUnitPrice * costFactors.quantity;

		if (recalcBasedOnMarkup) {
			totalCost = calculateTotalCostFromMarkup({ markup: costFactors.markup, rawCost, surcharge });

			updatedSellPrice = totalCost / costFactors.quantity;
		}
	} else {
		totalCost = addMarkup({ costBeforeMarkup: rawCost + surcharge, markup: costFactors.markup });
	}

	return {
		rawCost: roundAmount(rawCost, 2),
		totalCost: roundAmount(totalCost, 2),
		updatedSellPrice: updatedSellPrice ? roundAmount(updatedSellPrice, 2) : undefined,
	};
};

export const calculatePerEachEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors) => {
	let rawCost = costFactors.unitPrice * costFactors.quantity;
	const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

	const recalcBasedOnMarkup = costFactors.markup > 0 && rawCost > 0 && surcharge > 0 && costFactors.shouldRecalculateBasedOnMarkup;

	let totalCost = 0;
	let updatedSellPrice: number | undefined = undefined;
	if (costFactors.isSellPrice) {
		totalCost = rawCost;

		const vendorUnitPrice = Number(costFactors.vendorPrice ?? 0);
		rawCost = vendorUnitPrice * costFactors.quantity;

		if (recalcBasedOnMarkup) {
			totalCost = calculateTotalCostFromMarkup({ markup: costFactors.markup, rawCost, surcharge });

			updatedSellPrice = totalCost / costFactors.quantity;
		}
	} else {
		totalCost = addMarkup({ costBeforeMarkup: rawCost + surcharge, markup: costFactors.markup });
	}

	return {
		rawCost: roundAmount(rawCost, 2),
		totalCost: roundAmount(totalCost, 2),
		updatedSellPrice: updatedSellPrice ? roundAmount(updatedSellPrice, 2) : undefined,
	};
};

export const calculateLaborEstimateLineItemCost = (costFactors: IEstimateLineItemCostFactors): IEstimateLineItemCostResult | undefined => {
	if (costFactors.numberOfTechnicians) {
		const rawCostPerTech = costFactors.unitPrice * costFactors.quantity;

		const rawCost = rawCostPerTech * costFactors.numberOfTechnicians;

		const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

		const totalCost = addMarkup({ costBeforeMarkup: rawCost + surcharge, markup: costFactors.markup });

		return {
			rawCost: rawCost,
			totalCost: roundAmount(totalCost, 2),
		};
	} else {
		return undefined;
	}
};

export const calculatePurchaseOrderLineItemCost = (costFactors: IPurchaseOrderLineItemCostFactors): number | undefined => {
	switch (costFactors.priceType) {
		case 'HOURLY':
		case 'EACH':
			const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;

			const anticipatedCostRaw = costFactors.unitPrice * costFactors.quantity + surcharge;
			if (!Number.isNaN(anticipatedCostRaw)) {
				return Number(anticipatedCostRaw.toFixed(2));
			} else {
				return undefined;
			}
		case 'SQUAREFOOT':
			if (!Number.isNaN(costFactors.squareFeet)) {
				return calculateSquarefootPurchaseOrderLineItemCost(costFactors);
			} else {
				return undefined;
			}
		default:
			return undefined;
	}
};

export const calculateSquarefootPurchaseOrderLineItemCost = (costFactors: IPurchaseOrderLineItemCostFactors) => {
	const rawCost = calculateRawSquarefootLineItemCost({
		calculatedSquareFeet: costFactors.squareFeet,
		minimumSquareFeet: costFactors.minimumSquareFeet,
		quantity: costFactors.quantity,
		unitPrice: costFactors.unitPrice,
	});
	if (rawCost === undefined) return undefined;
	const surcharge = costFactors.surcharge ? costFactors.surcharge : 0;
	const anticipatedCost = rawCost + surcharge;
	return Number(anticipatedCost.toFixed(2));
};

export const calculateDepositAmount = (estimateTotal: number, depositPercent: number): number => {
	return roundAmount((estimateTotal * depositPercent) / 100, 2);
};

// NOTE: Typing getValues with (payload?: string | string[]) => Object turns into
// a whole todo with not a lot of payoff, so ts-ignore in the interest of prioritization
// @ts-ignore
export const calculateEstimateLineItemCostFromHookForm = (
	getValues,
	setValue,
	surchargePercent?: number,
	shouldRecalculateBasedOnMarkup?: boolean,
	isTotalCostOverridden?: boolean,
	fieldNamePrefix?: string
) => {
	const [
		price,
		vendorPrice,
		isSellPriceValue,
		priceType,
		quantity,
		unitPrice,
		squareFeet,
		markup,
		numberOfTechnicians,
		estimateLineItemType,
		surcharge,
		minimumSquareFeet,
		blockWidth,
		blockHeight,
		isSurchargeOverridden,
		isMarkupOverridden,
		length,
	] = getValues(
		getPrefixedNames(fieldNamePrefix || '', [
			'price',
			'vendorPrice',
			'isSellPrice',
			'priceType',
			'quantity',
			'unitPrice',
			'squareFeet',
			'markup',
			'numberOfTechnicians',
			'estimateLineItemType',
			'surcharge',
			'minimumSquareFeet',
			'blockWidth',
			'blockHeight',
			'isSurchargeOverridden',
			'isMarkupOverridden',
			'length',
		])
	);

	let rawCost: number | undefined;
	let totalCost: number | undefined;
	let updatedSellPrice: number | undefined;

	const isSellPrice = !!(price?.value?.sellPrice || price?.sellPrice || isSellPriceValue);

	switch (priceType) {
		case 'LENGTH':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined) {
				const costResult = calculateEstimateLineItemCost({
					length,
					priceType: priceType,
					quantity: quantity,
					unitPrice: unitPrice,
					vendorPrice: vendorPrice ?? price?.vendorPrice ?? price?.price ?? price?.value?.price,
					markup: markup,
					surcharge: surcharge,
					isSellPrice,
					shouldRecalculateBasedOnMarkup,
				});
				rawCost = costResult?.rawCost;
				totalCost = costResult?.totalCost;
				updatedSellPrice = costResult?.updatedSellPrice;

				break;
			}

			break;

		case 'EACH':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined) {
				const costResult = calculateEstimateLineItemCost({
					priceType: priceType,
					quantity: quantity,
					unitPrice: unitPrice,
					vendorPrice: vendorPrice ?? price?.vendorPrice ?? price?.price ?? price?.value?.price,
					markup: markup,
					surcharge: surcharge,
					isSellPrice,
					shouldRecalculateBasedOnMarkup,
				});
				rawCost = costResult?.rawCost;
				totalCost = costResult?.totalCost;
				updatedSellPrice = costResult?.updatedSellPrice;

				break;
			}
			break;
		case 'HOURLY':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined) {
				if (estimateLineItemType === 'LABOR' && numberOfTechnicians !== undefined) {
					const costResult = calculateEstimateLineItemCost({
						priceType: priceType,
						quantity: quantity,
						unitPrice: unitPrice,
						vendorPrice: price?.vendorPrice ?? price?.price ?? price?.value?.price,
						markup: markup,
						numberOfTechnicians: numberOfTechnicians,
						surcharge: surcharge,
						isSellPrice,
						shouldRecalculateBasedOnMarkup,
					});
					rawCost = costResult?.rawCost;
					totalCost = costResult?.totalCost;
					updatedSellPrice = costResult?.updatedSellPrice;
				} else {
					const costResult = calculateEstimateLineItemCost({
						priceType: priceType,
						quantity: quantity,
						unitPrice: unitPrice,
						vendorPrice: price?.vendorPrice ?? price?.price ?? price?.value?.price,
						markup: markup,
						estimateLineItemType: estimateLineItemType,
						surcharge: surcharge,
						isSellPrice,
						shouldRecalculateBasedOnMarkup,
					});
					rawCost = costResult?.rawCost;
					totalCost = costResult?.totalCost;
					updatedSellPrice = costResult?.updatedSellPrice;
				}
				break;
			} else {
				break;
			}
		case 'LINEAR_INCH':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined && squareFeet !== undefined) {
				const costResult = calculateEstimateLineItemCost({
					priceType: 'LINEAR_INCH',
					quantity,
					unitPrice,
					vendorPrice: vendorPrice ?? price?.vendorPrice ?? price?.price ?? price?.value?.price,
					blockSquareFeet: squareFeet,
					blockWidth,
					blockHeight,
					markup,
					surcharge,
					isSellPrice,
					shouldRecalculateBasedOnMarkup,
				});
				rawCost = costResult?.rawCost;
				totalCost = costResult?.totalCost;
				updatedSellPrice = costResult?.updatedSellPrice;

				break;
			} else {
				break;
			}
		case 'UNITED_INCH':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined && squareFeet !== undefined) {
				const costResult = calculateEstimateLineItemCost({
					priceType: 'UNITED_INCH',
					quantity,
					unitPrice,
					vendorPrice: vendorPrice ?? price?.vendorPrice ?? price?.price ?? price?.value?.price,
					blockSquareFeet: squareFeet,
					blockWidth,
					blockHeight,
					markup,
					surcharge,
					isSellPrice,
					shouldRecalculateBasedOnMarkup,
				});
				rawCost = costResult?.rawCost;
				totalCost = costResult?.totalCost;
				updatedSellPrice = costResult?.updatedSellPrice;

				break;
			} else {
				break;
			}
		case 'SQUAREFOOT':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined && squareFeet !== undefined) {
				const costResult = calculateEstimateLineItemCost({
					priceType: 'SQUAREFOOT',
					quantity,
					unitPrice,
					vendorPrice: vendorPrice ?? price?.vendorPrice ?? price?.price ?? price?.value?.price,
					blockSquareFeet: squareFeet,
					markup,
					surcharge,
					minimumSquareFeet,
					isSellPrice,
					shouldRecalculateBasedOnMarkup,
				});
				rawCost = costResult?.rawCost;

				totalCost = costResult?.totalCost;
				updatedSellPrice = costResult?.updatedSellPrice;

				break;
			} else {
				break;
			}
	}

	if (rawCost !== undefined && totalCost !== undefined) {
		setValue(getPrefixedName(fieldNamePrefix || '', 'rawCost'), rawCost);
		if (!isTotalCostOverridden) setValue(getPrefixedName(fieldNamePrefix || '', 'totalCost'), totalCost);

		if (isSellPrice) {
			if (shouldRecalculateBasedOnMarkup) {
				if (isNotNullish(updatedSellPrice)) setValue(getPrefixedName(fieldNamePrefix || '', 'unitPrice'), updatedSellPrice);
			} else if (!isMarkupOverridden) {
				setValue(
					getPrefixedName(fieldNamePrefix || '', 'markup'),
					calculateMarkup({
						sellPrice: totalCost,
						price: rawCost,
						surcharge: surcharge ? surcharge : 0,
					})
				);
			}
		}

		if (isNotNullish(surchargePercent) && !isSurchargeOverridden) {
			const calculatedSurcharge = calculateSurcharge(rawCost, surchargePercent);
			setValue(getPrefixedName(fieldNamePrefix || '', 'surcharge'), calculatedSurcharge);

			// TODOLINEITEM Once we update the surcharge, we need to recalculate the total cost.
			// For now this is just another call the the same function, but this should be cleaned up in the future.
			calculateEstimateLineItemCostFromHookForm(
				getValues,
				setValue,
				undefined,
				shouldRecalculateBasedOnMarkup,
				isTotalCostOverridden,
				fieldNamePrefix
			);
		}
	}
};

// NOTE ts-ignoring the getValues/setValue from hook form
// @ts-ignore
export const calculatePurchaseOrderLineItemCostFromHookForm = (
	getValues,
	setValue,
	surchargePercent?: number,
	isAnticipatedCostOverridden?: boolean
) => {
	const [priceType, quantity, unitPrice, squareFeet, surchargeFromForm, minimumSquareFeet] = getValues([
		'priceType',
		'quantity',
		'unitPrice',
		'squareFeet',
		'surcharge',
		'minimumSquareFeet',
	]);

	switch (priceType) {
		case 'EACH':
		case 'HOURLY':
			if (quantity !== undefined && quantity !== '' && unitPrice !== undefined) {
				const rawAnticipatedCost = Number(unitPrice) * Number(quantity);

				let surcharge = surchargeFromForm ? surchargeFromForm : 0;

				if (isNotNullish(surchargePercent)) {
					surcharge = calculateSurcharge(rawAnticipatedCost, surchargePercent);
				}

				setValue('surcharge', surcharge);

				const anticipatedCost = surcharge + rawAnticipatedCost;

				if (anticipatedCost && !isAnticipatedCostOverridden) {
					setValue('anticipatedCost', roundAmount(anticipatedCost, 2));
				}

				break;
			} else {
				break;
			}
		case 'SQUAREFOOT':
			const rawAnticipatedCost = calculateRawSquarefootLineItemCost({
				calculatedSquareFeet: squareFeet,
				minimumSquareFeet,
				quantity,
				unitPrice,
			});
			let surcharge = surchargeFromForm ? surchargeFromForm : 0;

			if (isNotNullish(surchargePercent)) {
				surcharge = calculateSurcharge(rawAnticipatedCost, surchargePercent);
			}

			setValue('surcharge', surcharge);

			const anticipatedCost = surcharge + rawAnticipatedCost;

			if (!Number.isNaN(anticipatedCost) && !isAnticipatedCostOverridden) {
				setValue('anticipatedCost', roundAmount(anticipatedCost, 2));
			}

			break;
	}
};

export function calculateFabricationCosts({
	fabrication,
	lineItemType,
	markup,
	price,
	squareFeet,
	surchargePercent,
}: {
	fabrication: IFabricationCostFactors;
	lineItemType: 'ESTIMATE' | 'PURCHASE_ORDER' | 'QUOTE_REQUEST';
	markup: number;
	price: { price: Prisma.Decimal | number; sellPrice?: Prisma.Decimal | number | null | undefined } | null;
	squareFeet: number | null;
	surchargePercent: Prisma.Decimal | number;
}) {
	const calculatedRawCost = calculateFabricationRawCostFromSupplierInventory(fabrication, squareFeet);
	const calculatedRawVendorCost = calculateFabricationRawCostFromSupplierInventory(
		{ ...fabrication, unitPrice: isNotNullish(price?.price) ? price.price : fabrication.unitPrice },
		squareFeet
	);

	if (calculatedRawCost !== null && calculatedRawVendorCost !== null) {
		const surcharge = calculateSurcharge(calculatedRawVendorCost, +surchargePercent);
		let calculatedMarkup = markup;
		let rawCost;
		let totalCost;

		if (isNotNullish(price?.sellPrice) && lineItemType === 'ESTIMATE') {
			totalCost = calculatedRawCost;
			rawCost = calculatedRawVendorCost;
			calculatedMarkup = calculateMarkup({ sellPrice: calculatedRawCost, price: calculatedRawVendorCost, surcharge });
		} else {
			totalCost = addMarkup({ costBeforeMarkup: calculatedRawCost + surcharge, markup });
			rawCost = calculatedRawCost;
		}

		return {
			rawCost,
			totalCost: Number(totalCost.toFixed(2)),
			markup,
			surcharge,
		};
	}

	return null;
}

function calculateFabricationRawCostFromSupplierInventory(fabrication: IFabricationCostFactors, squareFeet?: number | null): number | null {
	// NOTE This function is for fabrication form-level calculating
	if (fabrication.supplierInventory?.priceType && fabrication.quantity && isNotNullish(fabrication.unitPrice)) {
		if (fabrication.supplierInventory.priceType === 'LINEAR_INCH' || fabrication.supplierInventory.priceType === 'UNITED_INCH') {
			if (fabrication.length) {
				const rawCost = +fabrication.unitPrice * +fabrication.length * +fabrication.quantity;

				return Number(rawCost.toFixed(2));
			} else {
				return null;
			}
		} else if (
			fabrication.supplierInventory.priceType === 'EACH' ||
			(fabrication.supplierInventory.priceType === 'HOURLY' && fabrication.quantity)
		) {
			const rawCost = +fabrication.unitPrice * +fabrication.quantity;

			return Number(rawCost.toFixed(2));
		} else if (fabrication.supplierInventory.priceType === 'SQUAREFOOT' && squareFeet && fabrication.quantity) {
			const rawCost = +fabrication.unitPrice * +squareFeet * +fabrication.quantity;

			return Number(rawCost.toFixed(2));
		} else if (fabrication.supplierInventory.priceType === 'LENGTH' && fabrication.quantity && fabrication?.length) {
			const rawCost = +fabrication.unitPrice * +fabrication.length * +fabrication.quantity;

			return Number(rawCost.toFixed(2));
		} else if (fabrication.supplierInventory.priceType === 'PER_SQUARE') {
			const rawCost = +fabrication.unitPrice * muntinSquares(fabrication) * +fabrication.quantity;

			return Number(rawCost.toFixed(2));
		} else {
			return null;
		}
	} else {
		return null;
	}
}

const barCount = (bars: number | null | undefined): number => (Number.isFinite(bars) ? Math.max(Number(bars), 0) : 0);

export function muntinSquares({ horizontalBars, verticalBars }: IMuntinBars): number {
	return (barCount(horizontalBars) + 1) * (barCount(verticalBars) + 1);
}

export function calculateUnitedInch(width: number, height: number): number {
	return width + height;
}

export function calculateLinearInch(width: number, height: number): number {
	return (width + height) * 2;
}

export function calculateSurcharge(cost: number | undefined, surchargePercent: number) {
	const surchargeFloat = surchargePercent / 100;

	return roundAmount((cost ?? 0) * surchargeFloat, 2);
}

export function clamp({ value, min, max }: { value: number; min: number; max: number }): number {
	return Math.min(Math.max(value, min), max);
}

export function makeNegative(value: number): number {
	return -Math.abs(value);
}

export function msToDecimalHours(ms: number): number {
	return ms / (1000 * 60 * 60);
}

export function lineItemTotalCostWithFabrication(estimateLineItem: EstimateLineItemFull): number {
	const fabricationTotalCost = estimateLineItem.fabrications
		? estimateLineItem.fabrications.reduce((acc, fabrication) => {
				return acc + Number(fabrication.totalCost);
			}, 0)
		: 0;

	const res = Number(estimateLineItem.totalCost) + Number(fabricationTotalCost);

	return res;
}

export function isNumberGreaterThan32Bits(number: number) {
	const maxInt32Value = Math.pow(2, 31) - 1;
	const minInt32Value = -Math.pow(2, 31);

	return number > maxInt32Value || number < minInt32Value;
}

export function addTotalsToEstimateGroup(estimateGroup: EstimateForFocus['estimateGroups'][0], estimate: Estimate) {
	return {
		...estimateGroup,
		estimateLineItems: estimateGroup.estimateLineItems.map((estimateLineItem) => {
			const isTaxable = isNotNullish(estimateLineItem.isTaxable)
				? estimateLineItem.isTaxable
				: estimateLineItem.estimateLineItemType === EstimateLineItemType.SUPPLIER_INVENTORY;

			const isShowingInventory =
				(estimateLineItem.estimateLineItemType === EstimateLineItemType.GLASS ||
					estimateLineItem.estimateLineItemType === EstimateLineItemType.SUPPLIER_INVENTORY) &&
				!!estimateLineItem.price?.variantInventories?.length;

			if (!isShowingInventory) return { ...estimateLineItem, isTaxable };

			const onHand = estimateLineItem.price?.variantInventories?.[0]?.onHand ?? 0;
			const reservedAmount =
				onHand > 0 && isNotNullish(estimateLineItem.quantity) && estimate.estimateState === EstimateState.WON
					? Math.min(+estimateLineItem.quantity, onHand)
					: 0;
			const onReserve = estimateLineItem.isPickedFromInventory ? 0 : reservedAmount;
			const onHold =
				estimateLineItem.price?.estimateLineItems?.reduce((accumulator, estimateLineItem) => {
					if (estimateLineItem.estimateGroup.estimate.estimateState === EstimateState.WON) {
						return accumulator + (estimateLineItem.quantity ? +estimateLineItem.quantity : 0);
					}

					return accumulator;
				}, 0) ?? 0;
			const netOnHand = onHand - onHold;
			const onOrderForJob =
				estimateLineItem.price?.purchaseOrderLineItems?.reduce((accumulator, purchaseOrderLineItem) => {
					if (isNotNullish(estimate.jobId) && purchaseOrderLineItem.purchaseOrder?.jobId === estimate.jobId) {
						return accumulator + (purchaseOrderLineItem.quantity ? +purchaseOrderLineItem.quantity : 0);
					}

					return accumulator;
				}, 0) ?? 0;
			const totalOnOrder =
				estimateLineItem.price?.purchaseOrderLineItems?.reduce(
					(accumulator, purchaseOrderLineItem) => accumulator + (purchaseOrderLineItem.quantity ? +purchaseOrderLineItem.quantity : 0),
					0
				) ?? 0;

			return { ...estimateLineItem, isTaxable, isShowingInventory, onHand, onReserve, onHold, netOnHand, onOrderForJob, totalOnOrder };
		}),
		markupTotal: estimateGroup.estimateLineItems.reduce((acc, estimateLineItem) => {
			const lineItemMarkup = Number(estimateLineItem.totalCost) - Number(estimateLineItem.rawCost) - Number(estimateLineItem.surcharge);
			const fabricationTotalMarkup = estimateLineItem.fabrications
				? estimateLineItem.fabrications.reduce((accFabrication, fabrication) => {
						return accFabrication + (Number(fabrication.totalCost) - Number(fabrication.rawCost) - Number(fabrication.surcharge));
					}, 0)
				: 0;
			return acc + lineItemMarkup + fabricationTotalMarkup;
		}, 0),
		rawCostTotal: estimateGroup.estimateLineItems.reduce((acc, estimateLineItem) => {
			const fabricationRawCost = estimateLineItem.fabrications
				? estimateLineItem.fabrications.reduce((accFabrication, fabrication) => {
						return accFabrication + Number(fabrication.rawCost);
					}, 0)
				: 0;
			return acc + Number(estimateLineItem.rawCost) + fabricationRawCost;
		}, 0),
		surchargeTotal: estimateGroup.estimateLineItems.reduce((acc, estimateLineItem) => {
			const fabricationTotalSurcharge = estimateLineItem.fabrications
				? estimateLineItem.fabrications.reduce((accFabrication, fabrication) => {
						return accFabrication + Number(fabrication.surcharge);
					}, 0)
				: 0;

			return acc + Number(estimateLineItem.surcharge) + fabricationTotalSurcharge;
		}, 0),
	};
}
